import { Checkbox } from "@mantine/core";
import { Alert, Box, FormControl, FormControlLabel, FormHelperText, FormLabel, Grid2, List, ListItem, ListItemText, MenuItem, Paper, Radio, RadioGroup, Slider, Stack, Table, TableBody, TableCell, TableContainer, TableRow, TextField, Tooltip, ToggleButton, Typography } from "@mui/material";
import * as React from "react";
import { SelectValidator } from "react-material-ui-form-validator";
import { FormFieldKeyConditionEnum } from "../_common/enum/forms/fomFieldKeyCondition.enum";
import { FormFieldKeyConditionTypeEnum } from "../_common/enum/forms/fomFieldKeyConditionType.enum";
import { FormFieldValueTypeEnum } from "../_common/enum/forms/formFieldValueType.enum";
import { IFormFieldKeyManage } from "../_common/models/formFieldKey";
import { IFormFieldKeyLookupManage } from "../_common/models/formFieldKeyLookup";
import { IFormTemplateSectionFormManage } from "../_common/models/formTemplateSection/formTemplateSectionFormManage.model";
import { StandardButton } from "../_components/button/primary.button";
import { StandardTextValidator } from "../_components/textValidator/standardTextValidator";
import PaperHeading from "../_components/typography/paperHeading";
import { API_URL } from "../_config/config.const";
import { tokenService } from "../_services/token.service";
import { DataTypeAutoComplete } from "../_components/autocomplete/dataType.autocomplete";
import { IFormFieldManage } from "../_common/models/formField";
import { DataTypeMultipleAutoComplete } from "../_components/autocomplete/dataTypeMultiple.autocomplete";
import SignatureCanvas from "react-signature-canvas";

function SignatureFieldControl(props: {
    field: IFormFieldKeyManage;
    fieldId: number;
    handleChange: (event: any, ffkId: number) => void;
    isDisabled: boolean;
    isContinue: boolean;
    validators: string[];
    validatorMessages: string[];
    variant: string;
}) {
    const { field, fieldId, handleChange, isDisabled, isContinue, validators, validatorMessages, variant } = props;
    const sigRef = React.useRef<SignatureCanvas | null>(null);

    React.useLayoutEffect(() => {
        const v = field.formField?.fieldValue;
        if (!sigRef.current || !v || typeof v !== "string" || !v.startsWith("data:image")) return;
        if (!sigRef.current.isEmpty()) return;
        sigRef.current.fromDataURL(v);
    }, [field.formField?.fieldValue]);

    const onSignatureEnd = () => {
        const sig = sigRef.current;
        if (!sig) return;
        const v = sig.isEmpty() ? "" : sig.getTrimmedCanvas().toDataURL("image/png");
        handleChange({ target: { name: field.shortName, value: v } }, fieldId);
    };

    if (isDisabled) {
        return (
            <Box>
                <Typography variant="subtitle2">{field.label}</Typography>
                {field.description && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        {field.description}
                    </Typography>
                )}
                {field.formField?.fieldValue ? (
                    <Box
                        component="img"
                        src={field.formField.fieldValue}
                        alt={field.label}
                        sx={{ maxWidth: "100%", border: "1px solid", borderColor: "divider", borderRadius: 1 }}
                    />
                ) : (
                    <Alert severity="warning">{field.label} not provided</Alert>
                )}
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="subtitle2">{field.label}</Typography>
            {field.description && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {field.description}
                </Typography>
            )}
            <SignatureCanvas
                ref={sigRef}
                canvasProps={{
                    width: 500,
                    height: 200,
                    className: "signature-canvas",
                    style: { border: "1px solid #ccc", borderRadius: 4, maxWidth: "100%" },
                }}
                onEnd={onSignatureEnd}
            />
            <StandardButton
                type="button"
                icon="clear"
                variant="secondary"
                shrink
                onClick={() => {
                    sigRef.current?.clear();
                    handleChange({ target: { name: field.shortName, value: "" } }, fieldId);
                }}
            >
                Clear
            </StandardButton>
            {(field.isRequired && isContinue && !field.formField?.fieldValue) && (
                <Grid2 container>
                    <Grid2 size={{ xs: 12 }}>
                        <FormHelperText style={{ color: "#d32f2f" }}>This field is required</FormHelperText>
                    </Grid2>
                </Grid2>
            )}
            <StandardTextValidator
                label={field.label}
                value={field.formField?.fieldValue ?? ""}
                name={field.shortName}
                required={field.isRequired}
                hidden
                type="text"
                disabled={isDisabled}
                variant={variant as any}
                validators={validators}
                errorMessages={validatorMessages}
                onChange={(e: any) => handleChange(e, fieldId)}
            />
        </Box>
    );
}

export interface GetFieldParams {
    field: IFormFieldKeyManage;
    fieldId: number;
    isForReview: boolean;
    isLoading: boolean;
    isContinue: boolean;
    handleChange: (event: any, ffkId: number) => void;
    globalFormFields?: Array<IFormFieldManage>;
    checkIfLookupDependancyMet?: (ffkfl: IFormFieldKeyLookupManage) => boolean;
}

export function getField(params: GetFieldParams) {
    const {
        field,
        fieldId,
        isForReview,
        isLoading,
        isContinue,
        handleChange,
        globalFormFields,
        checkIfLookupDependancyMet: customCheckIfLookupDependancyMet
    } = params;

    // Use provided function or fall back to default
    const checkIfLookupDependancyMetFn = customCheckIfLookupDependancyMet ||
        ((ffkfl: IFormFieldKeyLookupManage) => checkIfLookupDependancyMet(ffkfl, globalFormFields));

    var validators: string[] = [];
    var validatorMessages: string[] = [];
    var isDisabled = isForReview;
    var variant = "outlined";

    if (field.isRequired) {
        validators.push("required");
        validatorMessages.push("This field is required");
    }

    switch (field.formFieldValueTypeId) {
        case (FormFieldValueTypeEnum.String):
            return <StandardTextValidator
                label={field.label}
                value={field.formField?.fieldValue ?? ""}
                name={field.shortName}
                required={field.isRequired}
                helperText={field.description}
                fullWidth
                disabled={isDisabled}
                type={"text"}
                variant={variant}
                validators={[...validators]}
                errorMessages={[...validatorMessages]}
                onChange={(e: any) => { handleChange(e, fieldId) }}
            />;
        case (FormFieldValueTypeEnum.Date):
            return <StandardTextValidator
                label={field.label}
                value={field.formField?.fieldValue ?? ""}
                name={field.shortName}
                required={field.isRequired}
                fullWidth
                helperText={field.description}
                type={"date"}
                disabled={isDisabled}
                variant={variant}
                validators={[...validators]}
                errorMessages={[...validatorMessages]}
                onChange={(e: any) => { handleChange(e, fieldId) }}
            />;
        case (FormFieldValueTypeEnum.Time):
            return <StandardTextValidator
                label={field.label}
                value={field.formField?.fieldValue ?? ""}
                name={field.shortName}
                required={field.isRequired}
                fullWidth
                helperText={field.description}
                type={"time"}
                disabled={isDisabled}
                variant={variant}
                shrink
                validators={[...validators]}
                errorMessages={[...validatorMessages]}
                onChange={(e: any) => { handleChange(e, fieldId) }}
            />;
        case (FormFieldValueTypeEnum.Email):
            return <StandardTextValidator
                label={field.label}
                value={field.formField?.fieldValue ?? ""}
                name={field.shortName}
                required={field.isRequired}
                fullWidth
                helperText={field.description}
                type={"email"}
                disabled={isDisabled}
                variant={variant}
                validators={[...validators, "isEmail"]}
                errorMessages={[...validatorMessages, 'Email is not valid']}
                onChange={(e: any) => { handleChange(e, fieldId) }}
            />;
        case (FormFieldValueTypeEnum.PhoneNumber):
            return <StandardTextValidator
                label={field.label}
                value={field.formField?.fieldValue ?? ""}
                name={field.shortName}
                required={field.isRequired}
                variant={variant}
                fullWidth
                helperText={field.description}
                type={"text"}
                disabled={isDisabled}
                validators={field.isRequired ? ['required'] : []}
                errorMessages={field.isRequired ? ['This field is required'] : []}
                onChange={(e: any) => { handleChange(e, fieldId) }}
            />;
        case (FormFieldValueTypeEnum.Dropdown):
            return <SelectValidator
                value={field.formField?.fieldValue ?? ""}
                onChange={(e: any) => { handleChange(e, fieldId) }}
                required={field.isRequired}
                label={field.label}
                name="formFieldValueTypeId"
                helperText={field.description}
                variant={variant}
                fullWidth
                disabled={isDisabled}
                validators={field.isRequired ? ['required'] : []}
                errorMessages={field.isRequired ? ['This field is required'] : []}
                InputLabelProps={{
                    shrink: !!field.formField?.fieldValue,
                }}>

                {field.formFieldKeyLookups?.filter(m => checkIfLookupDependancyMetFn(m)).map(lookup =>
                    <MenuItem key={"list_item_menu_" + field.id + "_" + lookup.id} value={lookup.lookupValue}>{lookup.label || lookup.lookupValue}</MenuItem>
                )}
            </SelectValidator>;
        case (FormFieldValueTypeEnum.Radio):
            return <FormControl>
                <FormLabel id={field.label}>{`${field.label} ${field.isRequired ? "*" : ""}`}</FormLabel>
                <RadioGroup
                    row
                    aria-labelledby={field.label}
                    onChange={(e: any) => { handleChange(e, fieldId) }}
                    name={`${field.shortName}`}>

                    {field.formFieldKeyLookups?.filter(m => checkIfLookupDependancyMetFn(m)).map(lookup =>
                        <>
                            <FormControlLabel
                                key={"list_item_radio_" + field.id + "_" + lookup.id}
                                required={field.isRequired}
                                checked={(!!field.formField?.fieldValue && lookup.lookupValue.toLowerCase() == field.formField?.fieldValue.toLowerCase()) ? true : false}
                                value={lookup.lookupValue}
                                label={lookup.label}
                                control={
                                    <Tooltip title={lookup.description}>
                                        <Radio disabled={isDisabled} key={"list_item_radio_sub_" + field.id + "_" + lookup.id} />
                                    </Tooltip>
                                }
                            />
                        </>
                    )}
                    {(field.isRequired && isContinue && !field.formField?.fieldValue) && <Grid2 container> <Grid2 size={{ xs: 12 }}><FormHelperText style={{ "color": "#d32f2f" }}>This field is required</FormHelperText></Grid2></Grid2>}
                </RadioGroup>

                <StandardTextValidator
                    label={field.label}
                    value={field.formField?.fieldValue ?? ""}
                    name={field.shortName}
                    required={field.isRequired}
                    hidden
                    type={"text"}
                    disabled={isDisabled}
                    variant={variant}
                    validators={field.isRequired ? ['required'] : []}
                    errorMessages={field.isRequired ? ['This field is required'] : []}
                    onChange={(e: any) => { handleChange(e, fieldId) }}
                />
            </FormControl>;
        case (FormFieldValueTypeEnum.Text):
            return (
                <Typography
                    variant="subtitle1"
                    sx={{
                        color: 'text.primary',
                        fontSize: '0.8rem',
                        fontStyle: 'italic'
                    }}
                >
                    {field.label}
                </Typography>
            );
        case (FormFieldValueTypeEnum.Textarea):
            return <StandardTextValidator
                label={field.label}
                value={field.formField?.fieldValue ?? ""}
                name={field.shortName}
                required={field.isRequired}
                fullWidth
                multiline
                helperText={field.description}
                type={"text"}
                rows={4}
                disabled={isDisabled}
                variant={variant}
                validators={field.isRequired ? ['required'] : []}
                errorMessages={field.isRequired ? ['This field is required'] : []}
                onChange={(e: any) => { handleChange(e, fieldId) }}
            />;
        case (FormFieldValueTypeEnum.Number):
            return <StandardTextValidator
                label={field.label}
                value={field.formField?.fieldValue ?? ""}
                name={field.shortName}
                required={field.isRequired}
                fullWidth
                helperText={field.description}
                type={"number"}
                disabled={isDisabled}
                variant={variant}
                validators={field.isRequired ? ['required'] : []}
                errorMessages={field.isRequired ? ['This field is required'] : []}
                onChange={(e: any) => { handleChange(e, fieldId) }}
            />;
        case (FormFieldValueTypeEnum.Signature):
            return (
                <SignatureFieldControl
                    field={field}
                    fieldId={fieldId}
                    handleChange={handleChange}
                    isDisabled={isDisabled}
                    isContinue={isContinue}
                    validators={[...validators]}
                    validatorMessages={[...validatorMessages]}
                    variant={variant}
                />
            );
        case (FormFieldValueTypeEnum.FileUpload):
            return <Grid2 size={{ xs: 12 }}>
                <Grid2 container>
                    <Grid2 size={{ xs: 6 }} >
                        {(!isForReview) && <TextField
                            className={"documentUpload"}
                            id={"documentUpload_" + field.id}
                            label={field.label}
                            defaultValue={""}
                            name={field.shortName}
                            required={field.isRequired && !field.formField?.id}
                            type={"file"}
                            disabled={isDisabled}
                            error={field.isRequired && !field.formField?.id && !field.formField?.fieldValue}
                            helperText={field.isRequired && !field.formField?.id && !field.formField?.fieldValue ? 'This field is required' : ""}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            onChange={(e: any) => { handleChange(e, fieldId) }}
                        />}
                    </Grid2>
                    <Grid2 size={{ xs: 6 }}>
                        {(!field.formField?.id) && (
                            <StandardTextValidator
                                label={field.label}
                                value={field.formField?.fieldValue ?? ""}
                                name={field.shortName}
                                required={field.isRequired}
                                hidden
                                type={"text"}
                                disabled={isDisabled}
                                variant={variant}
                                validators={field.isRequired ? ['required'] : []}
                                errorMessages={field.isRequired ? ['This field is required'] : []}
                                onChange={(e: any) => { handleChange(e, fieldId) }}
                            />
                        )}

                        {((field.formField?.id && field.formField?.fieldValue)) && (
                            <StandardButton icon={"download"} variant="primary" type="button" onClick={() => {
                                tokenService.GetReportToken().then((data) => {
                                    return window.open(API_URL + "/formField/GetUploadById/?token=" + data + "&fileId=" + field.formField?.id, "_blank");
                                });
                            }}>{field.shortName || field.label}
                            </StandardButton>
                        )}

                        {isForReview && (!field.formField?.id || !field.formField?.fieldValue) && (
                            <Alert severity="warning">{field.label} Not provided</Alert>
                        )}
                    </Grid2>
                </Grid2>
            </Grid2 >;
        case (FormFieldValueTypeEnum.ImageUpload):
            return (
                <Grid2 size={{ xs: 12 }}>
                    <Grid2 container>
                        <Grid2 size={{ xs: 6 }}>
                            {!isForReview && (
                                <TextField
                                    className={"documentUpload"}
                                    id={"documentUpload_" + field.id}
                                    label={field.label}
                                    defaultValue={""}
                                    name={field.shortName}
                                    required={field.isRequired && !field.formField?.id}
                                    type={"file"}
                                    disabled={isDisabled}
                                    error={field.isRequired && !field.formField?.id && !field.formField?.fieldValue}
                                    helperText={field.isRequired && !field.formField?.id && !field.formField?.fieldValue ? 'This field is required' : ""}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    onChange={(e: any) => { handleChange(e, fieldId) }}
                                />
                            )}
                        </Grid2>
                        <Grid2 size={{ xs: 6 }}>
                            {!field.formField?.id && (
                                <StandardTextValidator
                                    label={field.label}
                                    value={field.formField?.fieldValue ?? ""}
                                    name={field.shortName}
                                    required={field.isRequired}
                                    hidden
                                    type={"text"}
                                    disabled={isDisabled}
                                    variant={variant}
                                    validators={field.isRequired ? ['required'] : []}
                                    errorMessages={field.isRequired ? ['This field is required'] : []}
                                    onChange={(e: any) => { handleChange(e, fieldId) }}
                                />
                            )}

                            {((field.formField?.id && field.formField?.fieldValue)) && (
                                <StandardButton icon={"download"} variant="primary" type="button" onClick={() => {
                                    tokenService.GetReportToken().then((data) => {
                                        return window.open(API_URL + "/formField/GetUploadById/?token=" + data + "&fileId=" + field.formField?.id, "_blank");
                                    });
                                }}>{field.shortName || field.label}</StandardButton>
                            )}

                            {isForReview && (!field.formField?.id || !field.formField?.fieldValue) && (
                                <Alert severity="warning">{field.label} Not provided</Alert>
                            )}
                        </Grid2>
                    </Grid2>
                </Grid2>
            );

        case (FormFieldValueTypeEnum.List):
            return <>
                {field.showLabel && <Typography>
                    {field.label}
                </Typography>
                }

                <List sx={{ listStyle: "decimal", pl: 4 }}>
                    {field.formFieldKeyLookups?.filter(m => checkIfLookupDependancyMetFn(m)).map(lookup =>
                        <ListItem key={"list_item_list_" + lookup.id} sx={{ display: "list-item" }}>
                            <ListItemText primary={lookup.label || lookup.lookupValue} />
                        </ListItem>
                    )}
                </List></>;
        case (FormFieldValueTypeEnum.Title):
            return <PaperHeading>{field.label}</PaperHeading>;
        case (FormFieldValueTypeEnum.Image):
            return <img style={{
                width: "100%",
                marginTop: "10px",
                display: 'block',
                marginLeft: 'auto',
                marginRight: 'auto',
                marginBottom: "10px"
            }} src={"data:image/jpeg;charset=utf-8;base64," + field.label} alt={"test"}></img>;
        case (FormFieldValueTypeEnum.Checkbox):
            return <>
                {field.showLabel && <FormLabel id={field.label}>{field.label} {field.isRequired ? "*" : ""}</FormLabel>}
                {field.formFieldKeyLookups?.filter(m => checkIfLookupDependancyMetFn(m)).map(lookup =>
                    <Grid2 size={{ xs: 12 }} >


                        <FormControlLabel
                            disabled={isLoading || isForReview}
                            value={lookup.lookupValue}
                            required={field.isRequired}
                            style={{ "paddingLeft": "16px", "paddingTop": "4px" }}
                            onChange={(e: any) => { handleChange(e, fieldId) }}
                            checked={(!!field.formField?.fieldValue && field.formField?.fieldValue.toLowerCase() === lookup.lookupValue.toLowerCase()) ? true : false}
                            control={<Checkbox style={{ "paddingRight": "8px" }} value={lookup.lookupValue} color="primary" />}
                            label={lookup.label || lookup.lookupValue} />

                    </Grid2>
                )}

                {(field.isRequired && isContinue && !field.formField?.fieldValue) && <Grid2 container> <Grid2 size={{ xs: 12 }}><FormHelperText style={{ "color": "#d32f2f" }}>This field is required</FormHelperText></Grid2></Grid2>}
                <StandardTextValidator
                    label={field.label}
                    value={field.formField?.fieldValue ?? ""}
                    name={field.shortName}
                    required={field.isRequired}
                    hidden
                    type={"text"}
                    disabled={isDisabled}
                    variant={variant}
                    validators={field.isRequired ? ['required'] : []}
                    errorMessages={field.isRequired ? ['This field is required'] : []}
                    onChange={(e: any) => { handleChange(e, fieldId) }}
                />
            </>;
        case (FormFieldValueTypeEnum.FileDownload):
            return <StandardButton disabled={isForReview} icon={"download"} variant="primary" type="button" onClick={() => {
                tokenService.GetReportToken().then((data) => {
                    return window.open(API_URL + "/formField/GetDownloadById/?fileId=" + field.label + "&token=" + data, "_blank");
                });
            }}>{field.shortName || field.label}</StandardButton>;
        case (FormFieldValueTypeEnum.RadioButton):
            return <FormControl fullWidth>
                {field.showLabel && <FormLabel id={field.label}>{field.label} {field.isRequired ? "*" : ""}</FormLabel>}
                <Grid2 container spacing={1}>
                    {field.formFieldKeyLookups?.filter(m => checkIfLookupDependancyMetFn(m)).map(lookup =>
                        <Grid2 size={{ xs: (field.formFieldKeyLookups?.filter(m => checkIfLookupDependancyMet(m)).length || 0) % 8 == 0 ? 3 : (field.formFieldKeyLookups?.length || 0) % 7 == 0 ? 3 : (field.formFieldKeyLookups?.length || 0) % 6 == 0 ? 4 : (field.formFieldKeyLookups?.length || 0) % 5 == 0 ? 4 : (field.formFieldKeyLookups?.length || 0) % 4 == 0 ? 3 : (field.formFieldKeyLookups?.length || 0) % 3 == 0 ? 4 : (field.formFieldKeyLookups?.length || 0) % 2 == 0 ? 6 : 12 }} >
                            <ToggleButton fullWidth color="primary" onChange={(e: any) => { handleChange(e, fieldId) }} selected={!!field.formField?.fieldValue && field.formField?.fieldValue.toLowerCase() === lookup.lookupValue.toLowerCase()} value={lookup.lookupValue} aria-label="left aligned">
                                {lookup.label || lookup.lookupValue}
                            </ToggleButton>
                        </Grid2>
                    )}
                    {(field.isRequired && isContinue && !field.formField?.fieldValue) && <Grid2 container> <Grid2 size={{ xs: 12 }}><FormHelperText style={{ "color": "#d32f2f" }}>This field is required</FormHelperText></Grid2></Grid2>}
                    <StandardTextValidator
                        label={field.label}
                        value={field.formField?.fieldValue ?? ""}
                        name={field.shortName}
                        required={field.isRequired}
                        hidden
                        type={"text"}
                        disabled={isDisabled}
                        variant={variant}
                        validators={field.isRequired ? ['required'] : []}
                        errorMessages={field.isRequired ? ['This field is required'] : []}
                        onChange={(e: any) => { handleChange(e, fieldId) }}
                    />
                </Grid2>
            </FormControl >;
        case (FormFieldValueTypeEnum.Slider):
            return <>
                <FormControl fullWidth>
                    <FormLabel id={field.label}>{field.label}: {valuetext(Number(field.formField?.fieldValue || 0)).toString()}</FormLabel>
                    <Grid2 container spacing={6}>
                        <Grid2 size={{ xs: 6 }} >
                            <Slider
                                aria-label="Custom marks"
                                defaultValue={field.minValue || 0}
                                value={Number(field.formField?.fieldValue || "")}
                                onChange={(e: any) => { handleChange(e, fieldId) }}
                                getAriaValueText={valuetext}
                                step={1000}
                                min={field.minValue}
                                max={field.maxValue}
                                valueLabelDisplay="off"
                                name={field.shortName}
                                marks={getSliderMarks(field.minValue, field.maxValue)}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 2 }} >
                            <StandardTextValidator
                                value={field.formField?.fieldValue ?? "0"}
                                name={field.shortName}
                                required={field.isRequired}
                                fullWidth
                                type={"number"}
                                disabled={isDisabled}
                                variant={variant}
                                validators={field.isRequired ? ['required'] : []}
                                errorMessages={field.isRequired ? ['This field is required'] : []}
                                onChange={(e: any) => { handleChange(e, fieldId) }}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 2 }} >
                        </Grid2 >
                    </Grid2 >
                </FormControl >
            </>;
        case (FormFieldValueTypeEnum.WarningAlert):
            return <Alert severity="warning">{field.label}</Alert>;
        case (FormFieldValueTypeEnum.InfoAlert):
            return <Alert severity="info">{field.label}</Alert>;
        case (FormFieldValueTypeEnum.SuccessAlert):
            return <Alert severity="success">{field.label}</Alert>;
        case (FormFieldValueTypeEnum.DangerAlert):
            return <Alert severity="error">{field.label}</Alert>;
        case (FormFieldValueTypeEnum.VideoEmbed):
            return <iframe
                width="560"
                height="315"
                src={"https://www.youtube.com/embed/" + field.label} title="YouTube video player" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" />;
        case (FormFieldValueTypeEnum.DataTypeAutoComplete):
            return <>
                <DataTypeAutoComplete
                    dataType={field.dataType}
                    label={field.label}
                    valueField="name"
                    value={field.formField?.fieldValue ?? ""}
                    name={field.shortName}
                    required={field.isRequired}
                    disabled={isDisabled}
                    onChange={(e: any) => {
                        handleChange(e, fieldId)
                    }}
                />
            </>;
        case (FormFieldValueTypeEnum.DataTypeMultipleAutoComplete):
            return <>
                <DataTypeMultipleAutoComplete
                    dataType={field.dataType}
                    label={field.label}
                    valueField={field.dataTypeValueFieldName ?? "name"}
                    multiple
                    asCsv
                    autoSelectSingle={field.autoSelectSingle ?? false}
                    helperText={field.description}
                    hasParent={!!field.formFieldKeyDependencyId || (!!field.formFieldKeyParentDependencies && !!field.formFieldKeyParentDependencies.length)}
                    //parentId={getMultipleFromFieldValue(field.formFieldKeyParentDependencies)}
                    value={field.formField?.fieldValue ?? ""}
                    name={field.shortName}
                    required={field.isRequired}
                    disabled={isDisabled}
                    onChange={(e: any, item) => {
                        handleChange(e, fieldId)
                    }}
                />
            </>
            break;
        case (FormFieldValueTypeEnum.Table):
            // Table is just a container - actual rendering happens in formSection-manage
            return <div style={{ display: 'none' }} />;
        default: return null;
    }
}

export function valuetext(value: number): string {
    return `€${value.toLocaleString()}`;
}

export function getSliderMarks(minValue?: number, maxValue?: number): Array<{ value: number; label: string }> {
    minValue = minValue || 0;
    maxValue = maxValue || 9999;
    var marks: Array<{ value: number; label: string }> = [];

    var val = Math.ceil(maxValue / 4);
    for (var i = 0; i < 5; i++) {
        marks.push({
            value: val * i,
            label: valuetext(val * i),
        });
    }

    return marks;
}

export function checkIfLookupDependancyMet(
    ffkfl: IFormFieldKeyLookupManage,
    globalFormFields?: Array<IFormFieldManage>
): boolean {
    var _return = false;
    if (!ffkfl.conditionFormFieldKeyId) return true;

    var globalFormField = globalFormFields?.find(m => m.formFieldKeyId == ffkfl.conditionFormFieldKeyId);
    if (globalFormField && globalFormField.fieldValue && ffkfl.conditionFormFieldKeyValue &&
        globalFormField.fieldValue.toString().toLowerCase() === ffkfl.conditionFormFieldKeyValue.toString().toLowerCase()) {
        _return = true;
    }
    return _return;
}

export function checkCondition(
    _return: boolean | null,
    formFieldKey: any,
    ffkcd: any,
    field: any
): boolean | null {
    if (!field) return _return;

    if (_return === null ||
        (_return === true && formFieldKey.formFieldKeyConditionTypeId === FormFieldKeyConditionTypeEnum.All) ||
        (_return !== true && formFieldKey.formFieldKeyConditionTypeId === FormFieldKeyConditionTypeEnum.Any)) {
        if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.EqualsTo) {
            _return = (!!field.fieldValue && !!ffkcd.value) ? field.fieldValue.toString().toLowerCase() === ffkcd.value.toString().toLowerCase() : field.fieldValue === ffkcd.value;
        }
        else if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.EqualsOrGreater) {
            _return = Number(field.fieldValue ?? "0") >= Number(ffkcd.value ?? "0");
        }
        else if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.EqualsOrLess) {
            _return = Number(field.fieldValue ?? "0") <= Number(ffkcd.value ?? "0");
        }
        else if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.Less) {
            _return = Number(field.fieldValue ?? "0") < Number(ffkcd.value ?? "0");
        }
        else if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.Greater) {
            _return = Number(field.fieldValue ?? "0") > Number(ffkcd.value ?? "0");
        }
        else if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.Contains) {
            _return = field.fieldValue && ffkcd.value && field.fieldValue.trim().toLowerCase().includes(ffkcd.value.trim().toLowerCase());
        }
    }

    if (_return === null)
        _return = false;

    return _return;
}

export function checkIfDependancyMet(
    formFieldKey: IFormFieldKeyManage,
    data?: IFormTemplateSectionFormManage
): boolean {

    if ((!formFieldKey.formFieldKeyFormFieldKeyLookups || !formFieldKey.formFieldKeyFormFieldKeyLookups.length) &&
        (!formFieldKey.formFieldKeyConditionDependencies || !formFieldKey.formFieldKeyConditionDependencies.length))
        return true;

    if (formFieldKey.formFieldKeyFormFieldKeyLookups && formFieldKey.formFieldKeyFormFieldKeyLookups.length) {
        var _return: boolean | null = null;

        formFieldKey.formFieldKeyFormFieldKeyLookups.forEach(ffkfl => {
            if (data && data.formFieldKeys && data.formFieldKeys) {
                data.formFieldKeys.filter(ffk => ffk.formFieldKeyLookups).forEach(ffk => {
                    if (ffk.formFieldKeyLookups?.some(ffkl => ffkl.id == ffkfl.formFieldKeyLookupId)) {
                        if (!!ffk && !!ffk.formField && ffk.formField.fieldValue == ffkfl.value) _return = true;
                    }
                });

                var globalFormField = data?.globalFormFields?.find(m => m.formFieldKeyId == ffkfl.formFieldKeyLookupId);
                if (globalFormField && globalFormField.fieldValue && ffkfl && ffkfl.value &&
                    globalFormField.fieldValue.toString().toLowerCase() === ffkfl.value.toString().toLowerCase()) {
                    _return = true;
                }
            }
        });

        if (_return !== true) {
            _return = false;
            return false;
        }
    }

    if (formFieldKey.formFieldKeyConditionDependencies && formFieldKey.formFieldKeyConditionDependencies.length) {
        var _return: boolean | null = null;
        formFieldKey.formFieldKeyConditionDependencies.forEach((ffkcd: any) => {
            if (_return === true && formFieldKey.formFieldKeyConditionTypeId === FormFieldKeyConditionTypeEnum.Any)
                return;

            var localField = data?.formFieldKeys?.find(m => m.id == ffkcd.conditionFormFieldKeyId);
            var globalFormField = data?.globalFormFields?.find(m => m.formFieldKeyId == ffkcd.conditionFormFieldKeyId);

            if (!!globalFormField) {
                _return = checkCondition(_return, formFieldKey, ffkcd, globalFormField);
            }
            else if (!!localField) {
                _return = checkCondition(_return, formFieldKey, ffkcd, localField.formField);
            }
            else {
                _return = false;
                return;
            }
        });

        if (_return !== true) {
            _return = false;
            return false;
        }
    }

    return true;
}

export function getDependencyConditionText(
    formFieldKey: IFormFieldKeyManage
): Array<string> {

    var _return = Array<string>();

    if ((!formFieldKey.formFieldKeyFormFieldKeyLookups || !formFieldKey.formFieldKeyFormFieldKeyLookups.length) &&
        (!formFieldKey.formFieldKeyConditionDependencies || !formFieldKey.formFieldKeyConditionDependencies.length))
        return _return;

    if (formFieldKey.formFieldKeyFormFieldKeyLookups && formFieldKey.formFieldKeyFormFieldKeyLookups.length) {
        _return.push("Lookup Dependency: ");
        formFieldKey.formFieldKeyFormFieldKeyLookups.forEach(ffkfl => {
            _return.push(" -- " + ffkfl.formFieldKeyLookupLabel + " == " + ffkfl.value);
        });
    }

    if (formFieldKey.formFieldKeyConditionDependencies && formFieldKey.formFieldKeyConditionDependencies.length) {
        if (formFieldKey.formFieldKeyConditionTypeId === FormFieldKeyConditionTypeEnum.Any) _return.push("Condition Dependency ANY:");
        if (formFieldKey.formFieldKeyConditionTypeId === FormFieldKeyConditionTypeEnum.All) _return.push("Condition Dependency ALL:");

        formFieldKey.formFieldKeyConditionDependencies.forEach((ffkcd: any) => {
            var condition = "";
            switch (ffkcd.formFieldKeyConditionId) {
                case (FormFieldKeyConditionEnum.Contains): condition = "Contains"; break;
                case (FormFieldKeyConditionEnum.EqualsOrGreater): condition = ">="; break;
                case (FormFieldKeyConditionEnum.EqualsOrLess): condition = "<="; break;
                case (FormFieldKeyConditionEnum.EqualsTo): condition = "=="; break;
                case (FormFieldKeyConditionEnum.Greater): condition = ">"; break;
                case (FormFieldKeyConditionEnum.Less): condition = "<"; break;
            }

            _return.push(" -- " + ffkcd.conditionFormFieldKeyLabel + " " + condition + " " + ffkcd.value);
        });

    }
    return _return;

}

export function getSections(data?: IFormTemplateSectionFormManage): any[] {
    var subSections: any[] = [];
    if (data && data.formFieldKeys && data.formFieldKeys) {
        subSections = data.formFieldKeys.filter(ffk => ffk.formFieldValueTypeId === FormFieldValueTypeEnum.SubSection);
        if (data.formFieldKeys.some(ffk => ffk.formFieldValueTypeId !== FormFieldValueTypeEnum.SubSection && !ffk.subSectionId))
            subSections.push({});
    }

    return subSections || [{}];
}

export function getSectionKeys(data?: IFormTemplateSectionFormManage, subSectionId?: any): IFormFieldKeyManage[] {
    var keys: IFormFieldKeyManage[] = [];
    if (data && data.formFieldKeys && data.formFieldKeys) {
        return data.formFieldKeys.filter(ffk => ffk.formFieldValueTypeId !== FormFieldValueTypeEnum.SubSection && ffk.subSectionId == subSectionId);
    }

    return [];
}

export function isFormValid(
    data?: IFormTemplateSectionFormManage,
    checkIfDependancyMetFn?: (formFieldKey: IFormFieldKeyManage) => boolean
): boolean {
    if (!checkIfDependancyMetFn) {
        checkIfDependancyMetFn = (ffk) => checkIfDependancyMet(ffk, data);
    }

    var isFormValid = true;
    const sections = getSections(data);

    sections.filter(s => checkIfDependancyMetFn!(s)).forEach((s, index) => {
        const sectionKeys = getSectionKeys(data, s.id);
        sectionKeys?.filter((m: any) => checkIfDependancyMetFn!(m)).filter((m: any) => m.isRequired).forEach(ffk => {
            if (ffk.isRequired && (ffk.formField?.fieldValue === null || ffk.formField?.fieldValue === undefined || ffk.formField?.fieldValue === "")) {
                isFormValid = false;
            }
        });
    });

    return isFormValid;
}

export interface RenderTableParams {
    tableField: IFormFieldKeyManage;
    allAvailableFields: IFormFieldKeyManage[];
    data?: IFormTemplateSectionFormManage;
    editable?: boolean;
    onEditFieldClick?: (field: IFormFieldKeyManage) => void;
    onEmptyCellClick?: (tableField: IFormFieldKeyManage, row: number, col: number) => void;
    getField: (field: IFormFieldKeyManage, index: number) => React.ReactNode;
}

export function renderTable(params: RenderTableParams) {
    const { tableField, allAvailableFields, data, editable, onEditFieldClick, onEmptyCellClick, getField } = params;
    const rowNumber = Number(tableField.rowNumber || 1);
    const columnNumber = Number(tableField.columnNumber || 1);

    const tableFields = allAvailableFields
        .filter(ffk => {
            if (!ffk) return false;
            const belongsToTable = (ffk.subSectionId == tableField.id ||
                Number(ffk.subSectionId) === Number(tableField.id) ||
                String(ffk.subSectionId) === String(tableField.id));
            const isNotTableOrSubsection = ffk.formFieldValueTypeId !== FormFieldValueTypeEnum.Table &&
                ffk.formFieldValueTypeId !== FormFieldValueTypeEnum.SubSection;
            return belongsToTable && isNotTableOrSubsection;
        })
        .filter(ffk => checkIfDependancyMet(ffk, data));

    const tableCells: { [key: string]: IFormFieldKeyManage[] } = {};

    tableFields.forEach(ffk => {
        const row = Number(ffk.rowNumber || 1);
        const col = Number(ffk.columnNumber || 1);
        const key = `${row}_${col}`;
        if (!tableCells[key]) {
            tableCells[key] = [];
        }
        tableCells[key].push(ffk);
    });

    const tableContent = (
        <Box sx={{ mb: 3 }}>
            {(tableField.label && tableField.showLabel) && (
                <Typography
                    variant="h6"
                    sx={{
                        mb: 2,
                        mt: 1,
                        fontWeight: 600,
                        color: 'text.primary',
                        fontSize: '1.25rem'
                    }}
                >
                    {tableField.label}
                </Typography>
            )}
            {tableFields.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                    No fields assigned to this table. Fields need to have subSectionId matching this table's ID and rowNumber/columnNumber set.
                </Typography>
            )}
            <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                    mt: 2,
                    '& .MuiPaper-root': {
                        boxShadow: 2
                    }
                }}
            >
                <Table
                    sx={{
                        '& .MuiTableCell-root': {
                            border: '1px solid',
                            borderColor: 'divider',
                            verticalAlign: 'top',
                            minWidth: 150,
                            minHeight: 60,
                            px: 2,
                            py: 1.5
                        }
                    }}
                >
                    <TableBody>
                        {Array.from({ length: rowNumber }, (_, rowIndex) => {
                            const row = rowIndex + 1;
                            return (
                                <TableRow
                                    key={`table_row_${tableField.id}_${row}`}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'action.hover'
                                        }
                                    }}
                                >
                                    {Array.from({ length: columnNumber }, (_, colIndex) => {
                                        const col = colIndex + 1;
                                        const key = `${row}_${col}`;
                                        const cellFields = tableCells[key] || [];
                                        return (
                                            <TableCell
                                                key={`table_cell_${tableField.id}_${row}_${col}`}
                                                onClick={
                                                    editable
                                                        ? (e) => {
                                                            if (cellFields.length === 0 && onEmptyCellClick) {
                                                                e.stopPropagation();
                                                                onEmptyCellClick(tableField, row, col);
                                                            } else if (cellFields.length > 0) {
                                                                e.stopPropagation();
                                                            }
                                                        }
                                                        : undefined
                                                }
                                                sx={editable && cellFields.length === 0 && onEmptyCellClick ? {
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        backgroundColor: 'action.hover'
                                                    }
                                                } : undefined}
                                            >
                                                {cellFields.length > 0 ? (
                                                    <Stack spacing={cellFields.length > 1 ? 1.5 : 0}>
                                                        {cellFields.map((ffk, idx) => (
                                                            <Box key={`table_field_${ffk.id}_${idx}`}>
                                                                {getField(ffk, ffk.id)}
                                                            </Box>
                                                        ))}
                                                    </Stack>
                                                ) : editable && onEmptyCellClick ? (
                                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center' }}>
                                                        Click to add field
                                                    </Typography>
                                                ) : null}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    if (!editable) {
        return tableContent;
    }

    const isInteractiveElement = (target: EventTarget, currentTarget: HTMLElement) => {
        const element = target as HTMLElement;
        const tagName = element.tagName;
        if (['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'LABEL'].includes(tagName)) {
            return true;
        }
        if (element.closest('.MuiButtonBase-root')) {
            return true;
        }
        const fieldWrapper = element.closest('[data-field-wrapper="true"]');
        if (fieldWrapper && fieldWrapper !== currentTarget) {
            return true;
        }
        const tableCell = element.closest('td');
        if (tableCell) {
            if (tableCell.querySelector('input, textarea, select, button, .MuiButtonBase-root')) {
                return true;
            }
            if (onEmptyCellClick && !tableCell.querySelector('[data-field-wrapper="true"]')) {
                return true;
            }
        }
        return false;
    };

    return (
        <Box
            onClick={(e) => {
                if (!isInteractiveElement(e.target, e.currentTarget)) {
                    onEditFieldClick?.(tableField);
                }
            }}
            sx={{
                cursor: 'pointer',
                p: 0.5,
                borderRadius: 1,
                transition: 'background-color 0.2s',
                '&:hover': {
                    backgroundColor: 'action.hover'
                }
            }}
        >
            {tableContent}
        </Box>
    );
}

 